'use client';

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@ahafood/ui/components/carousel';
import { cn } from '@ahm/common-helpers';
import Autoplay from 'embla-carousel-autoplay';
import {
  useEffect,
  useMemo,
  useState,
  type ComponentProps,
  type ReactNode,
} from 'react';

/**
 * Snap horizontal carousel (Embla `slidesToScroll: 1`).
 * Dùng cho UI cần snap-to-slide: banner, quick-action.
 *
 * Replace `FreeModeSwiper` (enableSingMode mode) — same use case.
 */
interface SnapCarouselProps {
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  opts?: ComponentProps<typeof Carousel>['opts'];
  autoplay?: boolean;
  autoplayInterval?: number;
  dots?: boolean;
}

export function SnapCarousel({
  children,
  className,
  contentClassName,
  opts,
  autoplay = false,
  autoplayInterval = 3500,
  dots = false,
}: SnapCarouselProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [snaps, setSnaps] = useState<number[]>([]);
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    if (!api) return;
    setSnaps(api.scrollSnapList());
    setSelected(api.selectedScrollSnap());
    const onSelect = () => setSelected(api.selectedScrollSnap());
    const onReInit = () => {
      setSnaps(api.scrollSnapList());
      setSelected(api.selectedScrollSnap());
    };
    api.on('select', onSelect);
    api.on('reInit', onReInit);
    return () => {
      api.off('select', onSelect);
      api.off('reInit', onReInit);
    };
  }, [api]);

  const plugins = useMemo(
    () =>
      autoplay
        ? [Autoplay({ delay: autoplayInterval, stopOnInteraction: false })]
        : undefined,
    [autoplay, autoplayInterval]
  );

  return (
    <div className={className}>
      <Carousel
        opts={{ slidesToScroll: 1, loop: true, align: 'start', ...opts }}
        plugins={plugins}
        setApi={setApi}
      >
        <CarouselContent className={contentClassName}>
          {children}
        </CarouselContent>
      </Carousel>
      {dots && snaps.length > 1 ? (
        <div className="mt-3 flex justify-center gap-1.5">
          {snaps.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Slide ${i + 1}`}
              onClick={() => api?.scrollTo(i)}
              className={cn(
                'h-1.5 rounded-full transition-all',
                i === selected ? 'w-4 bg-primary' : 'w-1.5 bg-neutral-30'
              )}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

SnapCarousel.Item = CarouselItem;
